import cn from 'clsx';
import { FC, HTMLAttributes, ReactNode } from 'react';

import { ColorPalette } from '../../../../types';
import { colorToTailwindTheme } from '../../../../utils';
import Icon from '../../../Icon';

import s from './BasicListItem.module.scss';

type RightSideType = 'text' | 'badge' | 'button' | 'state' | 'icon' | 'checkmark' | 'checkbox' | 'radio' | 'switch' | 'expand';

interface RightSideModel {
  type: RightSideType;
  component: ReactNode;
}

interface TextModel {
  color?: ColorPalette;
  text: string;
}

interface TitleModel extends TextModel {
  badge?: ReactNode;
  titleStyle?: HTMLAttributes<HTMLSpanElement>['className'];
}

export interface BasicListItemProps {
  left?: ReactNode;
  overText?: TextModel;
  title: TitleModel;
  subText?: TextModel;
  right?: RightSideModel;
  listIcon?: boolean;
  border?: boolean;
}

const BasicListItem: FC<React.PropsWithChildren<React.PropsWithChildren<BasicListItemProps>>> = ({
  left,
  overText,
  title,
  subText,
  right,
  listIcon = false,
  border = false
}) => {
  const overTextColor = colorToTailwindTheme(overText?.color || 'gray.700', 'text');
  const titleColor = colorToTailwindTheme(title.color || 'gray.800', 'text');
  const subTextColor = colorToTailwindTheme(subText?.color || 'gray.700', 'text');
  const hasMaxWidth = right?.type === 'text' || right?.type === 'badge';
  const titleStyle = title.titleStyle ? title.titleStyle : 'body-16';

  return (
    <div
      className={cn(s.wrapper, {
        [s.border as string]: border
      })}
    >
      {left && <div className={s.left}>{left}</div>}

      <div className={s.center}>
        {overText?.text && <p className={cn(s.over_text, overTextColor)}>{overText.text}</p>}
        <p className={cn(titleStyle, titleColor)}>
          <span className={s.titleText}>{title.text}</span>
          {title?.badge}
        </p>
        {subText?.text && <p className={cn(s.subText, subTextColor)}>{subText.text}</p>}
      </div>

      {(right || listIcon) && (
        <div
          className={cn(s.right, {
            [s.max_width as string]: hasMaxWidth
          })}
        >
          {right && (
            <div
              className={cn(s.right_inner, {
                [s.ellipsis as string]: hasMaxWidth
              })}
            >
              {right.component}
            </div>
          )}
          {listIcon && <Icon type="listin-right" width={18} height={24} color="gray.300" />}
        </div>
      )}
    </div>
  );
};

export default BasicListItem;
